import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { isDisplayNameVisible } from 'store/modules/base/config/functions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import {
  getLocalParticipant,
  getParticipantDisplayName,
  isWhiteboardParticipant
} from 'store/modules/base/participants/functions';
import { isRemoteTrackMuted } from 'store/modules/base/tracks/functions';
import { isLayoutTileView } from 'store/modules/video-layout/functions';
import { getUserTimezone } from 'store/modules/login/functions';

const useStyles = makeStyles((theme) => {
  const { text01 } = theme.palette;
  return {
    badgeContainer: {
      alignItems: 'flex-end',
      display: 'inline-flex',
      justifyContent: 'end',
      flexDirection: 'column',
      marginBottom: theme.spacing(7),
      transition: 'margin-bottom 0.3s',
      pointerEvents: 'none',
      position: 'absolute',
      top: 10,
      right: 0,
      width: '100%',
      zIndex: 1
    },
    badge: {
      ...theme.typography.heading7,
      background: 'rgba(0, 0, 0, 0.1)',
      fontWeight: '300',
      borderRadius: '3px',
      color: text01,
      maxWidth: '50%',
      overflow: 'hidden',
      padding: '2px 16px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    badgeTime: {
      ...theme.typography.heading6,
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '3px',
      fontWeight: '300',
      color: text01,
      maxWidth: '50%',
      overflow: 'hidden',
      padding: '2px 16px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    badgeStatus: {
      ...theme.typography.labelSmall,
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '3px',
      fontWeight: '300',
      color: text01,
      maxWidth: '50%',
      overflow: 'hidden',
      padding: '2px 16px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    containerElevated: {
      marginBottom: theme.spacing(12)
    }
  };
});

/**
 * Component that renders the dominant speaker's name as a badge above the toolbar in stage view.
 * @param {IParticipant} largeVideoParticipant - Participant ID of the dominant speaker.
 *
 * @returns {ReactElement|null}
 * */
const ParticipantInfo = ({ largeVideoParticipant }) => {
  const classes = useStyles();

  const { clientHeight } = useSelector(
    (state) => state['features/base/responsive-ui']
  );
  const idleMode = useSelector(
    (state) => state['features/settings'].userIdleMode
  );
  const tracks = useSelector((state) => state['features/base/tracks']);
  const { rotationId } = useSelector((state) => state['features/large-video']);

  const { profiles } = useSelector((state) => state['features/login']);
  // const largeVideoParticipant: IParticipant = useSelector(getLargeVideoParticipant);
  const selectedId = largeVideoParticipant?.id;
  const nameToDisplay = useSelector((state) =>
    getParticipantDisplayName(state, selectedId)
  );
  const audioTrack = isRemoteTrackMuted(tracks, MEDIA_TYPE.AUDIO, selectedId);

  const localParticipant = useSelector(getLocalParticipant);
  const localId = localParticipant?.id;

  const isTileView = useSelector(isLayoutTileView);
  const showDisplayName = useSelector(isDisplayNameVisible);
  const profile =
    largeVideoParticipant && profiles
      ? profiles[largeVideoParticipant?.customUserid]
      : null;
  const [currentTime, setCurrentTime] = useState(
    moment().tz(getUserTimezone(profile)).locale('en').format('LT')
  );
  const intervalRef = useRef(null);
  const calculateCurrentTime = () => {
    const profile =
      largeVideoParticipant && profiles
        ? profiles[largeVideoParticipant?.customUserid]
        : null;
    const time = moment()
      .tz(getUserTimezone(profile))
      .locale('en')
      .format('LT');
    setCurrentTime(time);
  };
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    calculateCurrentTime();
    intervalRef.current = setInterval(() => calculateCurrentTime(), 60000);
    return () => clearInterval(intervalRef.current);
  }, [largeVideoParticipant, profiles]);
  if (
    showDisplayName &&
    nameToDisplay &&
    selectedId !== localId &&
    !isTileView &&
    !isWhiteboardParticipant(largeVideoParticipant)
  ) {
    return (
      <div className={`stage-participant-label ${classes.badgeContainer}`}>
        {profile && profile.device?.country && (
          <div
            className={classes.badge}
            style={{ color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white' }}>
            {profile.device?.country?.name}
          </div>
        )}
        {profile && profile.device?.city && (
          <div
            className={classes.badge}
            style={{ color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white' }}>
            {profile.device?.city?.name}
          </div>
        )}
        <div
          className={classes.badge}
          style={{ color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white' }}
          dangerouslySetInnerHTML={{ __html: nameToDisplay }}></div>
        {clientHeight > 500 && (
          <>
            <div
              className={classes.badgeTime}
              style={{
                color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white'
              }}>
              {currentTime}
            </div>
            <div
              className={classes.badgeStatus}
              style={{
                color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white'
              }}>
              their microphone is {audioTrack ? 'off' : 'on'}
            </div>
            <div
              className={classes.badgeStatus}
              style={{
                color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white'
              }}>
              their speaker is{' '}
              {largeVideoParticipant?.customFakeSpeaker == 'on' ? 'on' : 'off'}
            </div>
            {!largeVideoParticipant?.isRemote ? (
              <div
                className={classes.badgeStatus}
                style={{
                  color: idleMode ? 'rgba(255, 255, 255, 0.5)' : 'white'
                }}>
                video paired with you:{' '}
                {rotationId === largeVideoParticipant?.customUserid
                  ? 'yes'
                  : 'no'}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  }

  return null;
};

ParticipantInfo.propTypes = {
  largeVideoParticipant: PropTypes.object
};
export default React.memo(withBoundary(ParticipantInfo));
